<template lang="html">
  <div
    v-if="product"
    class="product-details w-full max-w-(--ui-container) pt-10 sm:m-auto"
  >
    <div class="mb-3 px-4 sm:px-0">
      <UButton
        :to="backLink"
        icon="i-lucide-chevron-left"
        variant="link"
        color="neutral"
        size="xs"
        class="h-auto p-0 text-xs text-muted"
      >
        Главная
      </UButton>
    </div>

    <div
      class="product-details__body mb-30 items-start gap-10 sm:grid sm:grid-cols-2"
    >
      <ProductGallery class="product-details__gallery" :value="product" />

      <div
        class="product-details__info flex flex-col gap-4 max-sm:p-4 sm:pt-20"
      >
        <div class="flex items-start justify-between gap-4">
          <h1 class="product-details__title text-2xl font-bold">
            {{ product?.wellKnownAttributes?.Name }}
          </h1>
          <UButton
            :icon="isFavorite ? 'i-heroicons-heart-solid' : 'i-lucide-heart'"
            :color="isFavorite ? 'error' : 'neutral'"
            variant="ghost"
            size="xl"
            @click.stop.prevent="onFavoriteToggle"
          />
        </div>

        <a
          v-if="ratingLink"
          href="#reviews"
          class="flex items-center gap-2 self-start text-sm"
        >
          <RatingStars :value="ratingLink.rating" />
          <span>{{ formatRating(ratingLink.rating) }}</span>
          <span class="text-muted underline">{{ reviewsCountLabel(ratingLink.count) }}</span>
        </a>

        <div class="flex flex-wrap items-center gap-2">
          <PromoBadge
            :badge="product?.promoBadge"
            :cart-promo="product?.hasCartPromo && !product?.hasCashbackPromo"
            :cashback="product?.hasCashbackPromo"
          />
        </div>

        <PromoPrice
          v-if="product"
          :value="product"
          class="product-details__price text-lg"
        />

        <PromoTermsAccordion
          v-if="product?.promoTerms"
          :terms="product.promoTerms"
        />

        <div
          v-if="!product?.inStock"
          key="out-of-stock"
          class="product-details__out-of-stock font-medium text-red-500"
        >
          Нет в наличии
        </div>

        <div
          v-if="product?.wellKnownAttributes?.Description"
          key="description"
          class="product-details__description whitespace-pre-wrap"
        >
          <div class="text-sm text-muted">
            Описание
          </div>
          <p>
            {{ product.wellKnownAttributes.Description }}
          </p>
        </div>

        <div
          v-if="product?.variants"
          key="variants"
          class="product-details__variants"
        >
          <VariantsNavigation :value="product" :active-id="id" />
        </div>

        <SizeTableSection class="self-end" :value="product" />

        <CartSection :value="product" />

        <AttributesSection :value="product" />

        <DocumentsSection :value="product" />
      </div>
    </div>

    <!-- Блок виден и без отзывов (count = 0): первый покупатель должен иметь возможность написать отзыв -->
    <ProductReviewsSection
      v-if="reviewsEnabled && productReviews?.summary"
      :key="id"
      :product-id="id"
      :summary="productReviews.summary"
      :initial-page="productReviews.productId === id ? productReviews.page : null"
      @reviewed="refreshReviews()"
    />
  </div>
</template>

<script setup lang="ts">
import { ReviewsListByProductRequestQuery, SortDirection } from '@repo/api/web/models'

import { useEcommerce } from '#Analytics/composables/useEcommerce'
import { mapCatalogProduct } from '#Analytics/utils/ecommerceMapper'
import AttributesSection from '#Products/components/Detail/AttributesSection.vue'
import CartSection from '#Products/components/Detail/CartSection.vue'
import DocumentsSection from '#Products/components/Detail/DocumentsSection.vue'
import ProductGallery from '#Products/components/Detail/ProductGallery.vue'
import SizeTableSection from '#Products/components/Detail/SizeTableSection.vue'
import VariantsNavigation from '#Products/components/Detail/VariantsNavigation.vue'
import PromoBadge from '#Promotion/components/PromoBadge.vue'
import PromoPrice from '#Promotion/components/PromoPrice.vue'
import PromoTermsAccordion from '#Promotion/components/PromoTermsAccordion.vue'
import ProductReviewsSection from '#Reviews/components/ProductReviewsSection.vue'
import RatingStars from '#Reviews/components/RatingStars.vue'
import { useReviews } from '#Reviews/composables/useReviews'
import { useReviewsEnabled } from '#Reviews/composables/useReviewsConfig'
import { formatRating, reviewsCountLabel } from '#Reviews/utils/reviewsFormat'
import { reviewsSeoOpts } from '#Reviews/utils/reviewsSeo'
import { DEFAULT_REVIEWS_SORT, parseReviewsSort, REVIEWS_FEED_LIMIT } from '#Reviews/utils/reviewsSort'

definePageMeta({
  name: 'ProductsDetails',
})

/** Сколько отзывов попадает в JSON-LD (`review`) */
const SEO_REVIEWS_LIMIT = 5

const { service, routing } = useProducts()
const route = useRoute()
const id = computed(() => routing.extractIdFromSlug(route.params.id as string))
const { toggle: toggleFavorite } = useFavoritesCore()

const backLink = computed(() =>
  route.query.category
    ? routing.getCatalogBySlug(String(route.query.category)).route
    : routing.getIndex().route,
)

const reviewsEnabled = useReviewsEnabled()
const { service: reviewsService } = useReviews()

const defaultReviewsSort = parseReviewsSort(DEFAULT_REVIEWS_SORT)

// Товар и отзывы грузятся параллельно. Отзывы: сводка для блока и первая страница ленты
// (с запросом ленты по умолчанию — лента её не перезапрашивает, первые SEO_REVIEWS_LIMIT
// идут в JSON-LD); сбой отзывов не ломает карточку — блока просто не будет
const [{ data: product }, { data: productReviews }] = await Promise.all([
  useAsyncData(
    `product-${id.value}`,
    () => service.get(id.value),
    { watch: [id] },
  ),
  useAsyncData(
    `product-reviews-summary-${id.value}`,
    async () => {
      if (!reviewsEnabled.value) return null
      const productId = id.value
      try {
        const [summary, page] = await Promise.all([
          reviewsService.summary(productId),
          reviewsService.listByProduct(productId, ReviewsListByProductRequestQuery.createLazy({
            Limit: REVIEWS_FEED_LIMIT,
            SortField: defaultReviewsSort.field,
            SortDirection: defaultReviewsSort.desc ? SortDirection.DESC : SortDirection.ASC,
          })),
        ])
        return { productId, summary, page }
      } catch {
        return null
      }
    },
    { watch: [id] },
  ),
])
if (!product.value) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Product not found',
  })
}

// SEO: 301 с «неправильного» слага на канонический (дубль-контент)
await useProductSlugRedirect(product)

useProductDetailsSeo(product, () => reviewsSeoOpts(
  productReviews.value?.summary,
  productReviews.value?.page.items?.slice(0, SEO_REVIEWS_LIMIT),
))

// «★ · N отзывов» под названием — только когда есть блок #reviews (сводка загружена) и отзывы уже есть.
// Оценка и число — из сводки: после нового отзыва при постмодерации они обновятся вместе с ней
const ratingLink = computed(() => {
  const summary = reviewsEnabled.value ? productReviews.value?.summary : undefined
  return summary && summary.count > 0 ? { rating: summary.rating, count: summary.count } : null
})

/** После нового отзыва — свежая сводка; при сбое остаётся прежняя, блок и ссылка не пропадают */
async function refreshReviews() {
  const current = productReviews.value
  if (!current) return
  try {
    const summary = await reviewsService.summary(current.productId)
    if (productReviews.value?.productId === current.productId)
      productReviews.value = { ...productReviews.value, summary }
  } catch (error) {
    console.warn('[reviews] не удалось обновить сводку отзывов:', error)
  }
}

const model = ref(product.value)

// Ecommerce: просмотр товара (detail) — на клиенте после загрузки; при смене
// варианта size/color грузится новая карточка (watch по id заказа события)
const ecommerce = useEcommerce()
onMounted(() => {
  if (product.value) ecommerce.detail(mapCatalogProduct(product.value))
})
watch(id, () => {
  if (product.value) ecommerce.detail(mapCatalogProduct(product.value))
})

const isFavorite = computed(() => model.value?.isFavorite)

async function onFavoriteToggle() {
  if (product.value) {
    await toggleFavorite(model.value)
  }
}
</script>
